<template>
  <div class="pricelistSection" :class="{ isPackage }">
    <div class="pricelistSection__Texts">
      <div>
        <div class="pricelistSection__Title">
          <h3 v-text="activePricelist.name" />
          <Tooltip v-if="activePricelist.description" :text="activePricelist.description">
            <template #icon>
              <Icon name="information" />
            </template>
          </Tooltip>
        </div>
        <div class="pricelistSection__KindsList">
          <template v-if="Array.isArray(data) && data.length > 1">
            <div class="pricelistSection__FieldWrapper" v-for="list in data">
              <RadioField v-model="selectedId" :value="list.external_id" :disabled="activePricelist.is_restricted" class="pricelistSection__RadioButton">
                {{ $t(`pricelistKinds.${list.kind.toLowerCase()}`) }}
  
                <div>
                  {{ formatPrice(list.price.amount, list.price.currency, lang) }}
                </div>
              </RadioField>

              <Tooltip v-if="accommodationMealPlans?.[list.kind] && Object.keys(accommodationMealPlans).length" :text="accommodationMealPlans[list.kind].description">
                <template #icon>
                  <Icon name="information" />
                </template>
              </Tooltip>
            </div>
          </template>
          <div v-else-if="!isPackage" class="pricelistSection__FieldWrapper">
            <div class="pricelistSection__SingleRateValue">
              <div>
                <Icon name="check-circle" />
                <template v-if="activePricelist?.kind">{{ $t(`pricelistKinds.${activePricelist.kind.toLowerCase()}`) }}</template>
              </div>

              <span>
                {{ formatPrice(activePricelist.price.amount, activePricelist.price.currency, lang) }}
              </span>
            </div>

            <Tooltip v-if="accommodationMealPlans?.[activePricelist?.kind] && Object.keys(accommodationMealPlans).length" :text="accommodationMealPlans[activePricelist?.kind].description">
              <template #icon>
                <Icon name="information" />
              </template>
            </Tooltip>
          </div>
        </div>
      </div>

      <div class="pricelistSection__RestrictionsWrapper">
        <div v-for="(value, key) in activePricelist.restriction">
          <template v-if="value">
            <span>
              <template v-if="isPackage && (key === 'date_from' || key === 'date_to')">
                {{ $t(`restrictions.${useCamelCase(key)}Package`) }}:
              </template>
              <template v-else>
                {{ $t(`restrictions.${useCamelCase(key)}`) }}:
              </template>
            </span>
            <template v-if="key.includes('stay')">
              {{ $t('restrictions.days', value) }}
            </template>
            <template v-else-if="isValidDate(value)">
              {{ format(new Date(value), 'dd. MM. yyyy.') }}
            </template>
            <template v-else>
              {{ value }}
            </template>
          </template>
        </div>
      </div>
    </div>

    <div class="pricelistSection__Reservation">
      <div class="pricelistSection__Prices">
        <p>
          <template v-if="activePricelist.is_loyality">{{ $t('loyaltyRate') }}</template>
        </p>
        <div>
          <p class="pricelistSection__PriceType">{{ $t(`prices.${formatStayTextId(activePricelist.price.price_type)}`) }}</p>
          <p class="pricelistSection__Price">
            <span v-if="activePricelist.standardRatePrice && !activePricelist.is_standard" class="pricelistSection__StandardPrice">
              {{ formatPrice(activePricelist.standardRatePrice.amount, activePricelist.standardRatePrice.currency, lang) }}
            </span>
            {{ formatPrice(activePricelist.price.amount, activePricelist.price.currency, lang) }}
          </p>

          <div v-if="activePricelist.total_price" class="pricelistSection__TotalPrice">
            <span>{{ $t('checkout.total') }} {{ formatPrice(activePricelist.total_price.amount, activePricelist.total_price.currency, lang) }}</span>

            <Tooltip v-if="activePricelist.breakdown_price?.length" class="pricelistSection__Tooltip">
              <ul>
                <li v-for="item in activePricelist.breakdown_price">
                  {{ format(new Date(item.date), 'dd. MM. yyyy.') }} - {{ formatPrice(item.price.amount, item.price.currency, lang) }}
                </li>
              </ul>
            </Tooltip>
          </div>
        </div>
      </div>
      <Button @click="prepareCheckoutData" medium theme-colors :disabled="activePricelist.is_restricted">{{ $t('makeReservation') }}</Button>
    </div>
  </div>
</template>

<script setup>
  import { format } from 'date-fns';

  const clearUnitsList = inject('clearUnitsList', false);
  const { link } = useSetup();
  const { status } = useAuth();
  const { lang } = useLanguage();
  const { setCleanFormData } = useCheckout();
  const { booking, constructAndUpdateQuery, units } = useBooking();
  const emit = defineEmits(['open-modal'])
  const props = defineProps({
    externalId: {
      type: String,
      default: '',
      required: true,
    },
    data: {},
    package: {
      type: Boolean,
      default: false,
    },
    unit: {
      type: String,
      default: '',
      required: true,
    },
    accommodationMealPlans: {
      type: Object,
      default: null,
    },
  })
  const activePricelist = ref(Array.isArray(props.data) ? props.data[0] : props.data)
  const selectedId = ref(activePricelist.value.external_id);

  const isPackage = computed(() => props.package);

  function prepareCheckoutData() {
    if (activePricelist.value.is_restricted) return;
    if (booking?.value?.loyalty_prices && JSON.parse(booking.value?.loyalty_prices) && status?.value !== 'authenticated') {
      emit('open-modal', { handleProceed: goToCheckout })
    } else {
      goToCheckout();
    }
  }

  async function goToCheckout() {
    // when this method is called from the AccommodationUnitList page, we need to clear the selected units list
    if (clearUnitsList) {
      setCleanFormData();
      booking.value.selected_units = [];
      units.value = [];
      sessionStorage.removeItem('units');
      sessionStorage.removeItem('selectedServices');
    }

    const _booking = JSON.parse(JSON.stringify(booking.value));

    // if this method is called from the CheckoutUnitList page, we need to update the selected units list with the new unit and guests data.
    // Guests data is written in the format: number of adults/number of children/children ages
    let guests = JSON.parse(sessionStorage.getItem('additionalRoomGuests'));
    const childrenAge = ![null, undefined].includes(guests) && guests.children_age ? guests?.children_age?.join(',') : _booking.children_age?.join(',') || [];
    const guestsData = {
      adults_count: guests?.adults_count || _booking.adults_count,
      children: guests?.children !== undefined ? guests.children : _booking.children,
      children_age: childrenAge,
    }

    // if there is only one selected unit, the value is a string, so we need to convert it to an array because the API expects an array
    if (typeof _booking.selected_units === 'string') {
      _booking.selected_units = [`${_booking.selected_units}/${guestsData.adults_count}/${guestsData.children}/${guestsData.children_age}`];
    }

    // prop.unit is room id - example: ROOM890
    _booking.selected_units = [..._booking.selected_units, `${props.unit}/${guestsData.adults_count}/${guestsData.children}/${guestsData.children_age}`];

    if (isPackage.value) {
      _booking.rate_external_id = '';
      _booking.package_external_id = selectedId.value;
    } else {
      _booking.package_external_id = '';
      _booking.rate_external_id = selectedId.value;
    }

    booking.value = JSON.parse(JSON.stringify(_booking));
    sessionStorage.removeItem('additionalRoomGuests');
    await constructAndUpdateQuery(link('Checkout'));
  }

  watch(() => selectedId.value, (newValue) => {
    const newActivePricelist = props.data.find((item) => item.external_id === newValue);
    if (newActivePricelist) activePricelist.value = newActivePricelist;
  });
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_pricelistSection.scss');
</style>